import React from 'react';

import { Spinner, Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { Translate, withI18nPlaceholders } from '@change-corgi/core-react/i18n';

import I18N_PLACEHOLDERS from '#config/i18n/placeholders/homepage/homepage.yml';

import { HeroTypingOverlay, PromptInput, PromptInputTextarea } from '#src/app/pages/homepage/shared/components';
import { useHeroTypingPlaceholder } from '#src/app/pages/homepage/shared/hooks';

import { ErrorMessage } from './components/ErrorMessage';
import { FormHelper } from './components/FormHelper';
import { LinkChip } from './components/LinkChip';
import { SubmitArea } from './components/SubmitArea';
import { useSocialPostUrlForm } from './hooks/useSocialPostUrlForm';

const SocialPostUrlFormComponent: React.FC = () => {
	const {
		data: { isSubmitting, isResolvingPreview, error, chip, multiSource, inlineMessages },
		actions: { onSubmit, onPaste, onInputChange, onRemoveChip },
	} = useSocialPostUrlForm();
	const {
		data: { value: prompt, placeholder, showOverlay, descriptionId },
		actions: { onValueChange: setPrompt },
	} = useHeroTypingPlaceholder();

	let promptContent: React.ReactNode;
	if (chip) {
		promptContent = <LinkChip {...chip} onRemove={onRemoveChip} data-qa="social-post-chip" />;
	} else {
		promptContent = (
			// fill the box so a click anywhere above the button focuses the textarea
			<div className="flex w-full grow flex-col">
				<PromptInputTextarea
					maxLength={500}
					name="prompt_or_url"
					placeholder={placeholder}
					aria-labelledby="social-post-input-heading"
					aria-describedby={showOverlay ? descriptionId : undefined}
					enterKeyHint="send"
					onPaste={onPaste}
					onChange={onInputChange}
					data-qa="social-post-prompt-textarea"
					className="grow pb-2"
				/>
				{showOverlay && <HeroTypingOverlay descriptionId={descriptionId} className="right-5" />}
				{isResolvingPreview && (
					<div className="flex items-center gap-2 px-5 pt-1 pb-2" data-qa="social-post-resolving">
						<Spinner className="size-4" />
						<Text variant="body" size="small" className="text-typography-weak">
							{multiSource ? (
								<Translate value="corgi.homepage.hero_section.input_resolving_link_label_multi_source" />
							) : (
								<Translate value="corgi.homepage.hero_section.input_resolving_link_label" />
							)}
						</Text>
					</div>
				)}
			</div>
		);
	}

	return (
		<div className="mb-3 flex w-full flex-col items-center justify-center lg:mb-3">
			<div className="flex w-full max-w-[500px] flex-col gap-4">
				<div>
					<Text
						className="text-center"
						variant="body"
						size="large"
						fontWeight="bold"
						as="h2"
						id="social-post-input-heading"
					>
						<Translate value="corgi.homepage.hero_section.input_prompt_label_alt" />
					</Text>
				</div>
				<div className="relative">
					<form onSubmit={onSubmit} data-qa="social-post-url-form" className="relative z-10">
						{/* min-h fits the tallest chip state so the box doesn't jump when the chip replaces the
						    textarea: a three-line TikTok title (~190px), or a two-line title plus description (~206px) */}
						<PromptInput
							data-qa="social-post-prompt"
							className={cn(
								'group elevation-low flex-col justify-between',
								multiSource ? 'min-h-[212px]' : 'min-h-[196px]',
							)}
							value={prompt}
							onValueChange={setPrompt}
						>
							{promptContent}
							<SubmitArea
								hasChip={!!chip}
								isSubmitting={isSubmitting}
								message={
									inlineMessages ? (
										// stays mounted when empty so screen readers announce a message as it appears
										<output
											data-qa="social-post-inline-message"
											aria-live="polite"
											className="flex min-w-0 items-start gap-2 pl-2"
										>
											{error && <ErrorMessage appearance="inline" error={error} multiSource={multiSource} />}
										</output>
									) : undefined
								}
							/>
						</PromptInput>
					</form>

					{/* hidden with inline messages, neutral helper copy included */}
					{!inlineMessages && <FormHelper error={error} multiSource={multiSource} />}
				</div>
			</div>
		</div>
	);
};

export const SocialPostUrlForm = withI18nPlaceholders(I18N_PLACEHOLDERS, SocialPostUrlFormComponent);
